import { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, TrendingDown, Ticket as TicketIcon, Banknote, XCircle, Download } from "lucide-react";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmt = (n) => (n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtI = (n) => (n || 0).toLocaleString("es-DO");

export default function Reports() {
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [exposure, setExposure] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const [r1, r2] = await Promise.all([
      api.get("/reports/sales", { params: { date_from: from, date_to: to } }),
      api.get("/reports/exposure", { params: { draw_date: to, top: 20 } }),
    ]);
    setData(r1.data);
    setExposure(r2.data.top);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const profitPct = useMemo(() => {
    if (!data || data.totals.sales === 0) return 0;
    return ((data.totals.profit / data.totals.sales) * 100).toFixed(1);
  }, [data]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Fecha", "Ventas", "Tickets"]];
    data.by_day.forEach(d => rows.push([d.date, d.sales, d.tickets]));
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ventas-${from}-${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Reportes"
        subtitle="Análisis de ventas, exposición y rentabilidad"
        testId="reports-header"
        actions={
          <Button variant="outline" onClick={exportCsv} className="rounded-sm border-slate-300" data-testid="reports-export">
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
        }
      />

      <div className="p-6 sm:p-8 space-y-6">
        {/* Range picker */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Desde</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm font-mono" data-testid="reports-from" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Hasta</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm font-mono" data-testid="reports-to" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-blue-600 hover:bg-blue-700 rounded-sm h-10" data-testid="reports-apply">
            {loading ? "Cargando…" : "Aplicar"}
          </Button>
          <div className="flex gap-2 ml-auto">
            {[
              { l: "Hoy", f: 0 }, { l: "7d", f: 7 }, { l: "30d", f: 30 },
            ].map((p) => (
              <button key={p.l} onClick={() => { setFrom(daysAgo(p.f)); setTo(todayStr()); }} className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid={`reports-preset-${p.l}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>

        {data && (
          <>
            {/* KPI */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Kpi icon={TrendingUp} label="Ventas" value={`RD$ ${fmt(data.totals.sales)}`} accent="text-blue-700" testId="kpi-sales" />
              <Kpi icon={Banknote} label="Premios pagados" value={`RD$ ${fmt(data.totals.payouts)}`} accent="text-green-700" testId="kpi-payouts" />
              <Kpi icon={data.totals.profit >= 0 ? TrendingUp : TrendingDown} label={`Utilidad (${profitPct}%)`} value={`RD$ ${fmt(data.totals.profit)}`} accent={data.totals.profit >= 0 ? "text-slate-950" : "text-red-700"} testId="kpi-profit" />
              <Kpi icon={TicketIcon} label="Tickets" value={fmtI(data.totals.tickets)} testId="kpi-tickets" />
              <Kpi icon={XCircle} label="Cancelados" value={fmtI(data.totals.cancelled)} accent="text-red-700" testId="kpi-cancelled" />
            </div>

            <Tabs defaultValue="day" className="w-full">
              <TabsList className="rounded-sm bg-slate-100 border border-slate-200">
                <TabsTrigger value="day" data-testid="tab-day">Por Día</TabsTrigger>
                <TabsTrigger value="branch" data-testid="tab-branch">Por Sucursal</TabsTrigger>
                <TabsTrigger value="user" data-testid="tab-user">Por Cajero</TabsTrigger>
                <TabsTrigger value="game" data-testid="tab-game">Por Juego</TabsTrigger>
                <TabsTrigger value="exposure" data-testid="tab-exposure">Exposición</TabsTrigger>
              </TabsList>

              <TabsContent value="day" className="mt-4">
                <ChartCard title="Ventas diarias" data={data.by_day.map(d => ({ label: d.date.slice(5), value: d.sales }))} />
                <TableView cols={["Fecha", "Ventas", "Tickets"]} rows={data.by_day.map(d => [d.date, `RD$ ${fmt(d.sales)}`, fmtI(d.tickets)])} />
              </TabsContent>
              <TabsContent value="branch" className="mt-4">
                <ChartCard title="Ventas por sucursal" data={data.by_branch.map(d => ({ label: d.branch_code, value: d.sales }))} />
                <TableView cols={["Código", "Ventas", "Tickets"]} rows={data.by_branch.map(d => [d.branch_code, `RD$ ${fmt(d.sales)}`, fmtI(d.tickets)])} />
              </TabsContent>
              <TabsContent value="user" className="mt-4">
                <ChartCard title="Ventas por cajero" data={data.by_user.slice(0, 10).map(d => ({ label: d.user_name, value: d.sales }))} />
                <TableView cols={["Cajero", "Ventas", "Tickets"]} rows={data.by_user.map(d => [d.user_name, `RD$ ${fmt(d.sales)}`, fmtI(d.tickets)])} />
              </TabsContent>
              <TabsContent value="game" className="mt-4">
                <ChartCard title="Ventas por juego" data={data.by_game.slice(0, 10).map(d => ({ label: d.game_name.slice(0, 14), value: d.sales }))} />
                <TableView cols={["Juego", "Ventas", "Jugadas"]} rows={data.by_game.map(d => [d.game_name, `RD$ ${fmt(d.sales)}`, fmtI(d.items)])} />
              </TabsContent>
              <TabsContent value="exposure" className="mt-4">
                <div className="text-xs text-slate-500 mb-3">Top 20 números con mayor exposición del día {to}</div>
                <TableView
                  cols={["Número", "Juego", "Tipo", "Jugadas", "Exposición"]}
                  rows={exposure.map(e => [
                    <span className="font-mono font-black text-lg">{e.number}</span>,
                    e.game_name || <span className="text-slate-400">—</span>,
                    e.play_type_name,
                    fmtI(e.plays),
                    `RD$ ${fmt(e.exposure)}`,
                  ])}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "text-slate-950", testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className={`text-2xl font-black font-mono ${accent}`}>{value}</div>
    </div>
  );
}

function ChartCard({ title, data }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">{title}</div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#64748b" />
            <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#64748b" />
            <Tooltip formatter={(v) => `RD$ ${fmt(v)}`} contentStyle={{ borderRadius: 2, border: "1px solid #e2e8f0", fontFamily: "JetBrains Mono", fontSize: 12 }} />
            <Bar dataKey="value" fill="#2563eb" radius={[2, 2, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={i === data.length - 1 ? "#1e40af" : "#2563eb"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TableView({ cols, rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            {cols.map((c) => <th key={c} className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={cols.length} className="p-8 text-center text-slate-500">Sin datos</td></tr>
          ) : rows.map((r, i) => (
            <tr key={`${r[0] ?? "row"}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
              {r.map((c, j) => <td key={`${i}-${j}`} className="p-3 text-slate-900 font-mono text-sm">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
